import {
  Award,
  Banknote,
  BatteryCharging,
  ClipboardCheck,
  Droplet,
  FileText,
  Flame,
  Home as HomeIcon,
  Layers,
  ListChecks,
  MapPin,
  Radio,
  ShieldCheck,
  ShieldOff,
  Sun,
  Thermometer,
  Wind,
  type LucideIcon,
} from "lucide-react";
import type { LabelledCalloutItem } from "@/components/landing/labelled-callout-section";
import heroAshpAsset from "@/assets/air-source-heat-pumps/hero-ashp.jpg.asset.json";
import ashpCardSiting from "@/assets/air-source-heat-pumps/ashp-card-siting.jpg";
import happyHomeownerFamily from "@/assets/lp/happy-homeowner-family.webp";
import boilerCardAgeing from "@/assets/lp/boiler/boiler-card-ageing.webp";
import quotationSchedule from "@/assets/lp/quotation-schedule-of-works.webp";
import postExtractionSurveyor from "@/assets/lp/post-extraction-surveyor.webp";
import busGrantJourney from "@/assets/boiler-upgrade-scheme/bus-grant-journey.png";
import { SITE } from "@/lib/site";

export const HERO_IMAGE = heroAshpAsset.url;
export const BUS_HERO_FLOAT_IMAGES = [
  { src: HERO_IMAGE, slug: "bus-hero-ashp" },
  { src: ashpCardSiting, slug: "bus-ashp-siting" },
  { src: happyHomeownerFamily, slug: "bus-homeowners" },
  { src: boilerCardAgeing, slug: "bus-boiler" },
  { src: quotationSchedule, slug: "bus-grant-paperwork" },
  { src: postExtractionSurveyor, slug: "bus-surveyor" },
  { src: busGrantJourney, slug: "bus-grant-journey" },
];
export const SOURCE_PAGE = "/boiler-upgrade-scheme";
export const PHONE_TEL = "tel:+441656339844";

export const HERO_TRUST = [
  "£7,500 BUS Grant (Up To £9,000 For Oil & LPG)",
  "Free Home Energy Survey",
  "Air Source Heat Pump Conversion",
  "MCS-Accredited Install Route",
];

// ---- Content ------------------------------------------------------------

export const TRUST_ITEMS = [
  { Icon: ShieldCheck, text: "MCS Accredited Route" },
  { Icon: ClipboardCheck, text: "CRS Energy Design And Install" },
  { Icon: Award, text: "Grant Guidance Included" },
  { Icon: MapPin, text: "South Wales Coverage" },
];

export const SERVICES = [
  {
    Icon: Thermometer,
    title: "Air Source Heat Pumps",
    line: "Replace fossil fuel heating with a lower-carbon electric heating system.",
    bestFor: "Homes using gas, oil, LPG or electric heating.",
    bullets: [
      "£7,500 to £9,000 BUS grant may apply (subject to eligibility and scheme budget)",
      "Designed using heat-loss calculations",
      "Installed through an MCS accredited route",
    ],
    cta: "Check Heat Pump Grant",
  },
  {
    Icon: Sun,
    title: "Solar PV Panels",
    line: "Generate your own electricity from daylight and reduce grid reliance.",
    bestFor: "Homes with suitable roof space and orientation.",
    bullets: [
      "Designed around your roof and usage",
      "Can be paired with battery storage",
      "Standalone or part of a wider plan",
    ],
    cta: "Explore Solar",
  },
  {
    Icon: BatteryCharging,
    title: "Battery Storage",
    line: "Store unused solar electricity and use more of your own energy later.",
    bestFor: "Homes generating solar or planning to.",
    bullets: [
      "Use more of your own solar energy",
      "Support evening and overnight usage",
      "Sized to your daily energy use",
    ],
    cta: "Explore Battery",
  },
  {
    Icon: Layers,
    title: "Heat Pump + Solar + Battery",
    line: "A combined system for a lower-carbon, lower-reliance home energy plan.",
    bestFor: "Homeowners wanting a long-term renewable strategy.",
    bullets: [
      "Lower-carbon heating plus self-generation",
      "Designed as one connected system",
      "Survey-led, not a generic package",
    ],
    cta: "Book A Full Survey",
  },
];

export const TESTIMONIALS = [
  { name: "Frank Ciccotti", date: "July 2026", source: "trustpilot" as const, text: "Highly competent work, from initial design to installation. We had an air source heat pump, solar panels and extensive insulation installed. Large teams of skilled tradespeople arrived in planned succession. Work was completed within agreed timescales. Problems, where they arose, were quickly resolved. They even painted after completing the work!" },
  { name: "Ken", date: "May 2024", source: "google" as const, text: "I contacted my local MP Elwyn Vaughn who gave me the name of a company to contact, who then put me in touch with Complete Remedial Solutions in Bridgend. This company took over the installation to finally sort out the problems and get the system up and running today Wednesday 29/05/2024. The manager Rui and the electricians Mike and Mathew who I would recommend to anyone needing a heating upgrade installation, have been absolutely first class in their care and work for help in sorting things out. I can't thank you enough guys. If I could give more than 5 stars I would." },
  { name: "Polly", date: "September 2023", source: "trustpilot" as const, text: "Very helpful, friendly and reliable. I used CRS in spring 2023 to do a fairly big retrofit including installing an Air Source Heat Pump, completely new 'wet' central heating system and a small solar panel array. Keith and the team were really helpful and friendly throughout and although there were a few hiccups and delays for various reasons, they kept communicating with me which really helped, until it was all completed. I wouldn't hesitate to use them again." },
];

export const SURVEY_STEPS = [
  { Icon: HomeIcon, label: "Check Your Home" },
  { Icon: Thermometer, label: "Calculate Heat Loss" },
  { Icon: Radio, label: "Review Radiators And Hot Water" },
  { Icon: Sun, label: "Check Solar And Battery Suitability" },
  { Icon: Banknote, label: "Explain Funding Routes" },
  { Icon: ListChecks, label: "Recommend Next Step" },
];

export const SUITABILITY_CHECKS = [
  { Icon: Thermometer, title: "Room-By-Room Heat Loss", detail: "We measure how much heat each room loses through walls, floors, ceilings and windows so the system can be sized accurately, not over- or under-specified." },
  { Icon: Radio, title: "Radiator Output", detail: "Existing radiators are checked against the lower flow temperatures a heat pump runs at, so we can flag any that may need upsizing for comfortable warmth." },
  { Icon: Droplet, title: "Hot Water Cylinder", detail: "We assess whether your current cylinder is heat-pump compatible, correctly sized for your household, and well insulated, or whether a replacement should be quoted." },
  { Icon: Layers, title: "Loft And Wall Insulation", detail: "We inspect loft depth and wall construction (cavity, solid or timber) to confirm whether your home retains heat well enough for a heat pump to run efficiently." },
  { Icon: Wind, title: "Draughts And Glazing", detail: "Window type, seals, doors and obvious draught paths are reviewed, since uncontrolled air leakage is one of the biggest causes of heat pump underperformance." },
  { Icon: MapPin, title: "Outdoor Unit Location", detail: "We identify a suitable position for the external unit considering airflow, noise to neighbours, pipework runs and permitted development requirements." },
  { Icon: Flame, title: "Current Heating Fuel", detail: "Your existing fuel (oil, LPG, gas, electric) shapes the running-cost comparison and confirms which grant routes, including the Boiler Upgrade Scheme, may apply, subject to eligibility." },
  { Icon: Sun, title: "Solar And Battery Potential", detail: "We check roof orientation, pitch, shading and consumer unit space to see whether solar PV and battery storage could pair with the heat pump to lower running costs." },
];

export const FUNDING = [
  { Icon: Banknote, title: "BUS Grant", helps: "Air source heat pump", support: "£7,500", keyPoint: "We apply as your installer and deduct the full grant from your quote upfront.", accent: false },
  { Icon: Flame, title: "Oil / LPG Uplift", helps: "Eligible oil or LPG heated homes", support: "£9,000 (21 July 2026 to 31 March 2027)", keyPoint: "Timing and eligibility subject to scheme rules.", accent: true },
];

export const GRANT_PROCESS_ITEMS: LabelledCalloutItem[] = [
  {
    id: "grant-survey",
    number: 1,
    title: "Free Survey & Eligibility Check",
    support: "Confirm your grant route",
    tooltip:
      "We survey your home, run the heat-loss calculations and confirm which grant route applies.",
    anchor: { x: 50, y: 10 },
    labelPos: { x: 52, y: 4 },
  },
  {
    id: "grant-quote",
    number: 2,
    title: "Your Quote, With The Grant Already Deducted",
    support: "Deducted before you pay",
    tooltip:
      "The full grant value is deducted from your quote upfront - before you pay anything. That's £7,500 for most homes, or £9,000 for eligible oil and LPG heated homes from 21 July 2026. It's a scheme requirement, not a favour.",
    anchor: { x: 50, y: 30 },
    labelPos: { x: 52, y: 22 },
  },
  {
    id: "grant-ofgem",
    number: 3,
    title: "We Apply To Ofgem On Your Behalf",
    support: "Installer-led application",
    tooltip:
      "As the MCS-certified installer, CRS Energy submits the grant application and all supporting evidence. Homeowners can't apply directly - the scheme is installer-led.",
    anchor: { x: 50, y: 50 },
    labelPos: { x: 52, y: 42 },
  },
  {
    id: "grant-email",
    number: 4,
    title: "You Confirm One Email From Ofgem",
    support: "14 days to respond",
    tooltip:
      "Ofgem will email you directly (from boiler.upgrade.scheme.ofgem@notifications.service.gov.uk) to confirm your consent and verify your identity. This is genuine and important - you have 14 days to respond, and the application can't proceed without it. We'll remind you when it's due.",
    anchor: { x: 50, y: 70 },
    labelPos: { x: 52, y: 62 },
  },
  {
    id: "grant-install",
    number: 5,
    title: "We Install, Commission & Claim The Grant",
    support: "Nothing extra for you to do",
    tooltip:
      "Once your voucher is issued, we install and commission your system to MCS standards, then claim the grant directly from Ofgem. Nothing extra for you to do.",
    anchor: { x: 50, y: 90 },
    labelPos: { x: 52, y: 80 },
  },
];

export type EligibilityBullet = string | { text: string; eligible: boolean };

export type EligibilityCard = {
  Icon: LucideIcon;
  title: string;
  intro: string;
  bullets: EligibilityBullet[];
  image: string;
  imageAlt: string;
};

export const ELIGIBILITY_CARDS: EligibilityCard[] = [
  {
    Icon: HomeIcon,
    title: "You Own The Property",
    intro:
      "Owner-occupiers, landlords, second-home and park-home owners, and small non-residential properties in England and Wales can all qualify.",
    bullets: [
      "Owner-occupiers and landlords",
      "Second homes and park homes",
      "Small non-residential properties",
      "England and Wales only",
    ],
    image: happyHomeownerFamily,
    imageAlt: "Homeowners outside their UK property",
  },
  {
    Icon: Flame,
    title: "Replacing Fossil Fuel Or Electric Heating",
    intro:
      "Gas, oil, LPG or coal boilers, or electric heating like storage heaters and electric boilers. Homes that already heat with a heat pump aren't eligible.",
    bullets: [
      "Gas, oil, LPG or coal boilers",
      "Storage heaters and electric boilers",
      "Must be replacing the existing system",
      "Existing heat-pump homes aren't eligible",
    ],
    image: boilerCardAgeing,
    imageAlt: "Ageing gas boiler due for replacement",
  },
  {
    Icon: ShieldOff,
    title: "Had Grant Work Before? You May Still Qualify",
    intro:
      "Most previous energy grants won't affect your BUS application. We simply need to check that your property hasn't already received public funding for a renewable heating system.",
    bullets: [
      { text: "ECO gas boiler? Still eligible", eligible: true },
      { text: "Loft or cavity insulation grants? Still eligible", eligible: true },
      { text: "Existing grant-funded heat pump? Not eligible", eligible: false },
      { text: "Previous BUS grant? Not eligible", eligible: false },
    ],
    image: quotationSchedule,
    imageAlt: "Grant scheme paperwork and records check",
  },
  {
    Icon: FileText,
    title: "No EPC Required",
    intro:
      "Since 28 April 2026 you no longer need a valid Energy Performance Certificate to apply. If your home doesn't have one, we gather the alternative evidence for you as part of the survey.",
    bullets: [
      "EPC no longer mandatory from 28 April 2026",
      "Alternative evidence gathered by us",
      "Handled as part of your free survey",
      "No extra hoops for homeowners",
    ],
    image: postExtractionSurveyor,
    imageAlt: "Surveyor gathering alternative evidence during a home survey",
  },
];

export const SURVEY_INCLUDES = [
  "Current heating review",
  "BUS grant route check",
  "Heat-loss and radiator review",
  "Solar and battery suitability",
  "Insulation and efficiency advice",
  "Clear next-step recommendation",
];


export const FAQ_CATEGORIES = [
  {
    key: "heatpumps",
    label: "Heat Pumps",
    items: [
      { q: "Will a heat pump increase my electric bill?", a: "A heat pump uses electricity, so your electricity use may increase. However, it replaces or reduces your use of fossil fuel heating such as gas, oil or LPG. The overall running cost depends on your home's heat loss, system design, radiator sizing, electricity tariff and how the system is used. This is why we complete a survey and heat-loss assessment before recommending a system." },
      { q: "Are air source heat pumps noisy?", a: "Modern air source heat pumps are designed to operate quietly, but the exact noise level depends on the unit, location and installation design. During the survey, we consider where the outdoor unit could be placed to reduce disturbance and meet relevant requirements." },
      { q: "Where will the heat pump go?", a: "The outdoor unit usually needs a suitable external space with airflow, access for maintenance and appropriate distance from openings or boundaries. We confirm this during the survey." },
      { q: "Will I need to replace my radiators?", a: "Possibly, but not always. Heat pumps often work at lower flow temperatures than traditional boilers, so some rooms may need larger radiators or additional heat emitters. CRS Energy checks room-by-room heat loss and radiator output before recommending changes." },
      { q: "Will I need a new hot water cylinder?", a: "Some homes may need a compatible hot water cylinder as part of the installation. This depends on your current system, available space and hot water requirements." },
      { q: "Do heat pumps work in older homes?", a: "Yes, many older homes can use heat pumps if the system is correctly designed. The key is understanding heat loss, insulation levels, radiator output and the correct system size." },
      { q: "Do I need insulation before a heat pump?", a: "Not always, but insulation can improve comfort and help the system run more efficiently. If your home is losing too much heat, we will explain whether insulation should be considered before or alongside a heat pump." },
      { q: "Will I lose cupboard or garden space?", a: "Possibly. Some installations require space for a hot water cylinder, pipework or an outdoor unit. During the survey, we explain where equipment could go and what space may be affected." },
    ],
  },
  {
    key: "grants",
    label: "Grants And Funding",
    items: [
      { q: "What is the Boiler Upgrade Scheme?", a: "The Boiler Upgrade Scheme is a government-backed grant that helps eligible property owners in England and Wales replace fossil fuel heating systems with low-carbon heating such as an air source heat pump." },
      { q: "How much is the heat pump grant?", a: "The current grant for an air source heat pump is £7,500, subject to eligibility and scheme budget. Eligible oil and LPG heated homes can access an enhanced £9,000 grant from 21 July 2026 to 31 March 2027, subject to scheme rules." },
      { q: "Do I receive the grant money directly?", a: "No. The installer applies for the grant and deducts the value from your quote if the installation is eligible." },
      { q: "Who can qualify for BUS?", a: "You usually need to own the property, be in England or Wales, replace fossil fuel heating or eligible electric heating, and use an MCS-certified installer. The property and installation must meet scheme rules." },
      { q: "Can landlords apply?", a: "Landlords may be able to apply if the property and heating system meet the scheme requirements. The survey helps confirm the likely route." },
      { q: "What if I do not qualify for BUS?", a: "We can still discuss solar, battery storage, insulation or other suitable upgrade options depending on your home and goals." },
      { q: "How much does a heat pump installation cost?", a: "The final cost depends on property size, heat pump size, radiator changes, cylinder requirements, pipework, access and whether wider upgrades are needed. Eligible homes may receive a £7,500 BUS grant (or £9,000 for eligible oil and LPG homes from 21 July 2026), subject to eligibility and scheme rules." },
      { q: "Who actually applies for the grant?", a: "We do. The Boiler Upgrade Scheme is installer-led - as your MCS-certified installer, CRS Energy submits the application to Ofgem with all the supporting evidence. Homeowners can't apply directly. Your only job is to confirm one consent email from Ofgem." },
      { q: "Do I receive the grant money?", a: "No - and that's a good thing. The full grant value (£7,500, or £9,000 for eligible oil and LPG homes) is deducted from your quote before you pay anything. We claim it back from Ofgem after your system is installed and commissioned. You never handle the money and never repay anything." },
      { q: "I've had an email from Ofgem - is it genuine?", a: "Almost certainly, yes. After we submit your application, Ofgem emails you directly from boiler.upgrade.scheme.ofgem@notifications.service.gov.uk to confirm your consent and verify your identity. You have 14 days to respond, and your application can't proceed without it. We'll always tell you when to expect it. If you're ever unsure, call us and we'll check." },
      { q: "Do I need an Energy Performance Certificate (EPC)?", a: "No. Since 28 April 2026, a valid EPC is no longer required to apply. If your home doesn't have one, we gather the alternative evidence Ofgem accepts - such as a recent utility bill and photos of your existing heating system - as part of your survey." },
      { q: "Can landlords or second-home owners get the grant?", a: "Yes. Owner-occupied homes, rented properties, second homes, holiday homes and park homes in England and Wales can all qualify, along with small non-residential properties. Landlords must let tenants know about the installation." },
      { q: "Can I get the BUS grant if I've had ECO or another grant?", a: "It depends what the funding was for. If ECO or another scheme paid for a previous gas boiler, that doesn't affect you - you can still get the BUS grant to replace it with a heat pump. What's not allowed is double-funding: the property can't have already had a BUS grant or public funding for a low-carbon heating system (like a heat pump under ECO or RHI), and your new heat pump can't be funded through ECO and BUS at the same time." },
      { q: "What happens if the scheme budget runs out?", a: "Ofgem can only issue vouchers while budget is available; if it runs out, applications join a queue until more budget is released. Once a voucher has been issued, the funding for it is secured. This is why no installer can honestly 'guarantee' a grant - we'll always give you a realistic picture." },
      { q: "Can I keep my wood-burning stove, immersion heater or storage heaters?", a: "In most cases, yes. The heat pump must fully replace your old fossil fuel or electric heating system, but since April 2026 the scheme allows non-fossil-fuel supplementary heating - wood-burning stoves, immersion heaters, storage heaters and similar - to be kept or added alongside it." },
      { q: "Will Ofgem contact me or visit my home?", a: "Possibly. Ofgem verifies every homeowner's identity as part of the application, and may contact you or request access to your property to audit grant-funded installations. It's a routine safeguard on public money, and we'll support you through it if it happens." },
      { q: "Is a new-build or self-build home eligible?", a: "Developer new-builds aren't eligible. Genuine self-builds and custom-builds - built mainly with the first owner's own resources or labour and never owned by a company - can qualify for the heat pump grant. It's assessed case by case, so ask us at survey." },
      { q: "How long is the grant voucher valid?", a: "Three months for an air source heat pump (six for ground source). The installation must be completed and the claim submitted within that window - which is why we schedule your install around the voucher, not the other way round." },
    ],
  },
  {
    key: "solar",
    label: "Solar And Battery",
    items: [
      { q: "How do solar panels work?", a: "Solar panels generate electricity from daylight. This electricity can be used by your home, exported to the grid or stored in a battery if you have one installed." },
      { q: "Do solar panels work with a heat pump?", a: "Yes. Solar panels can help generate electricity for the home, and this can support a household using an electric heat pump. The benefit depends on system size, usage patterns, roof suitability and whether battery storage is included." },
      { q: "What does battery storage do?", a: "A battery stores unused electricity generated by your solar panels so you can use more of your own energy later, such as in the evening." },
      { q: "Is battery storage covered by BUS?", a: "No. Battery storage is not funded through the Boiler Upgrade Scheme. It can be supplied and fitted as part of a wider renewables plan." },
      { q: "Can I have solar without a heat pump?", a: "Yes. Solar PV can be installed as a standalone upgrade if your roof and electrical setup are suitable." },
      { q: "Can I add a battery later?", a: "In many cases, yes, depending on the system design. We can discuss whether installing battery storage now or later makes more sense for your home." },
    ],
  },
  {
    key: "survey",
    label: "Survey And Installation",
    items: [
      { q: "What happens after I submit the form?", a: "A member of the team will contact you to confirm basic details, understand your goals and arrange the next step for your free home energy survey." },
      { q: "Is the survey really free?", a: "Yes, the initial home energy survey is free. Any paid work, installation quote or further technical work will be explained clearly before you proceed." },
      { q: "Do I have to go ahead after the survey?", a: "No. The survey helps you understand your options. There is no obligation to proceed." },
      { q: "Which areas do you cover?", a: "CRS Energy supports homeowners across South Wales, including Cardiff, Newport, Swansea, Bridgend, Barry, Penarth, Pontypridd, Caerphilly, Cwmbran, Torfaen and surrounding areas." },
      { q: "Who installs the system?", a: "CRS Energy supports the design and installation process through the appropriate certified route, including heat-loss calculations, system sizing, installation planning and aftercare." },
      { q: "How long does a heat pump installation take?", a: "Most installations take 2 to 5 days depending on property size, radiator changes, cylinder work and complexity." },
      { q: "Will I be without heating or hot water?", a: "There may be some disruption during installation. We explain the expected timeline before work starts so you know when heating or hot water may be temporarily affected." },
      { q: "Can you tell me exactly what I qualify for online?", a: "We can give an initial indication, but final eligibility and suitability depend on property details, heating type, survey findings and scheme rules." },
    ],
  },
];

export const BOILER_UPGRADE_SCHEME_FAQS = FAQ_CATEGORIES.flatMap((c) =>
  c.items.map((i) => ({ question: i.q, answer: i.a })),
);

